import ReactJson from '@microlink/react-json-view';
import { Modal, toast } from '@solinkcorp/solink-components';
import moment from 'moment-timezone';
import React, { useCallback, useMemo } from 'react';
import useLocale from 'src/hooks/useLocale';
import CommonUtil from 'utils/CommonUtil';
import { DEFAULT_TIMEZONE } from 'vd-constants';

interface JSONModalProps {
  src: object;
  isModalOpen: boolean;
  closeModal: () => void;
  title?: string;
  timezone?: string;
}

const LOCAL_TIME_FIELDS = new Set(['startTimeLocal', 'endTimeLocal']);

export const serializeDates = (value: unknown, timezone: string, key?: string): unknown => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return key && LOCAL_TIME_FIELDS.has(key)
      ? moment.tz(value, timezone).toISOString(true)
      : value.toISOString();
  }

  if (Array.isArray(value)) {
    return value.map((item) => serializeDates(item, timezone));
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([entryKey, entry]) => [
        entryKey,
        serializeDates(entry, timezone, entryKey),
      ]),
    );
  }
  return value;
};

const JSONModal: React.FC<JSONModalProps> = ({
  src,
  isModalOpen,
  closeModal,
  title = '',
  timezone,
}) => {
  const locale = useLocale();
  const serializedSrc = useMemo(
    () => serializeDates(src, timezone || DEFAULT_TIMEZONE) as object,
    [src, timezone],
  );
  const copyJSONToClipboard = useCallback(() => {
    const data = JSON.stringify(serializedSrc, null, 2);
    CommonUtil.copyStringToClipboard({
      string: data,
      successCallback: () => {
        toast.success('Copied JSON to clipboard');
      },
      errorCallback: () => {
        toast.error('Failed to copy JSON to clipboard');
      },
    });
  }, [serializedSrc]);

  return (
    <Modal
      actions={[
        {
          isPrimary: false,
          text: locale.general.cancel,
          onClick: closeModal,
        },
        {
          isPrimary: true,
          text: 'Copy to Clipboard',
          onClick: copyJSONToClipboard,
        },
      ]}
      title={`${title} JSON Data`}
      isOpen={isModalOpen}
      colorMode="dark"
      type="double-wide"
      onClose={closeModal}
    >
      <ReactJson
        src={serializedSrc}
        name={false}
        collapsed={1} // Collapse top-level objects by default
        enableClipboard={true}
        theme="tube"
        escapeStrings={false}
        displayDataTypes={false}
      />
    </Modal>
  );
};

export default JSONModal;
